import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-conditions-page">
      {/* Breadcrumb */}
      <div className="bg-[#FFF9F5] border-b">
        <div className="container-main py-4">
          <nav className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-brand-orange">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 font-medium">Terms & Conditions</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="bg-gradient-to-br from-[#FFF9F5] to-[#FFE8D4] py-12">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Terms & Conditions
            </h1>
            <p className="text-gray-600">Last updated: January 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-main">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
                <p className="text-gray-600 leading-relaxed">
                  By accessing or using NexaFusion's website (nexafusion.store), you agree to be bound by these Terms & Conditions. If you disagree with any part of these terms, you may not access our website or make purchases from us.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Products and Services</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  NexaFusion sells pet toys and related products. We make every effort to display our products accurately, but we cannot guarantee that your computer monitor's display of any color will be accurate.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We reserve the right to limit the quantities of any products or services that we offer. All descriptions of products and product pricing are subject to change at any time without notice, at our sole discretion.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Orders and Payments</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>We reserve the right to refuse any order you place with us</li>
                  <li>Prices for our products are subject to change without notice</li>
                  <li>We accept Cash on Delivery (COD) as our primary payment method</li>
                  <li>Payment is due upon delivery of your order</li>
                  <li>You agree to provide current, complete, and accurate purchase and account information</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Shipping and Delivery</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We ship to addresses within the United States only. Shipping times are estimates and are not guaranteed. We are not responsible for delays caused by shipping carriers, weather, or other circumstances beyond our control.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Risk of loss and title for items purchased from us pass to you upon delivery of the items to the carrier.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Intellectual Property</h2>
                <p className="text-gray-600 leading-relaxed">
                  All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of NexaFusion and is protected by United States and international copyright laws. You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">User Conduct</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  You agree not to:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Use our website for any unlawful purpose</li>
                  <li>Attempt to gain unauthorized access to any portion of the website</li>
                  <li>Interfere with or disrupt the website or servers</li>
                  <li>Submit false or misleading information</li>
                  <li>Upload or transmit viruses or malicious code</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Disclaimer of Warranties</h2>
                <p className="text-gray-600 leading-relaxed">
                  Our website and products are provided "as is" without any warranties, express or implied. We do not warrant that our website will be uninterrupted, secure, or error-free. We do not warrant that our products will meet your specific requirements or expectations.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
                <p className="text-gray-600 leading-relaxed">
                  To the fullest extent permitted by law, NexaFusion shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our website or products. Our total liability shall not exceed the amount you paid for the product(s) in question.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
                <p className="text-gray-600 leading-relaxed">
                  You agree to indemnify and hold harmless NexaFusion and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising out of your use of our website or violation of these Terms & Conditions.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
                <p className="text-gray-600 leading-relaxed">
                  These Terms & Conditions shall be governed by and construed in accordance with the laws of the State of New York, United States, without regard to its conflict of law provisions.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
                <p className="text-gray-600 leading-relaxed">
                  We reserve the right to update or modify these Terms & Conditions at any time without prior notice. Your continued use of the website after any changes constitutes your acceptance of the new terms.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                <p className="text-gray-600 leading-relaxed">
                  If you have any questions about these Terms & Conditions, please contact us:
                </p>
                <div className="mt-4 bg-[#FFF9F5] p-6 rounded-2xl">
                  <p className="text-gray-700"><strong>NexaFusion</strong></p>
                  <p className="text-gray-600">8 E 23nd St #4692</p>
                  <p className="text-gray-600">New York, NY 10365</p>
                  <p className="text-gray-600">United States</p>
                  <p className="text-gray-600 mt-2">Email: support@nexafusion.store</p>
                  <p className="text-gray-600">Phone: +1-888-728-1766</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditionsPage;
